import type { PackageDetails } from './S3Client';

const PACKAGE_TREE_CACHE_VERSION = 1;
const PACKAGE_TREE_CACHE_TTL_MS = 60 * 1000;

export interface PackageTreeCacheEntry {
  readonly branch: PackageDetails;
  readonly versions: PackageDetails[];
}

interface SerializedPackageDetails {
  readonly folder: string;
  readonly label: string;
  readonly timestamp?: number;
  readonly dateLabel?: string;
}

interface SerializedPackageTree {
  readonly version: number;
  readonly fetchedAt: number;
  readonly entries: Array<{
    readonly branch: SerializedPackageDetails;
    readonly versions: SerializedPackageDetails[];
  }>;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isSerializedPackageDetails = (value: unknown): value is SerializedPackageDetails => {
  if (!isRecord(value) || typeof value.folder !== 'string' || typeof value.label !== 'string') {
    return false;
  }

  const hasValidTimestamp = value.timestamp === undefined
      || (typeof value.timestamp === 'number' && Number.isFinite(value.timestamp));
  const hasValidDateLabel = value.dateLabel === undefined || typeof value.dateLabel === 'string';
  return hasValidTimestamp && hasValidDateLabel;
};

const isSerializedPackageTree = (value: unknown): value is SerializedPackageTree => {
  if (!isRecord(value)
      || value.version !== PACKAGE_TREE_CACHE_VERSION
      || typeof value.fetchedAt !== 'number'
      || !Number.isFinite(value.fetchedAt)
      || !Array.isArray(value.entries)) {
    return false;
  }

  return value.entries.every(entry => {
    if (!isRecord(entry) || !isSerializedPackageDetails(entry.branch) || !Array.isArray(entry.versions)) {
      return false;
    }

    return entry.versions.every(isSerializedPackageDetails);
  });
};

export class PackageTreeCache {
  private readonly storageKey: string;
  private readonly storage: Storage | null;

  constructor(cacheKey: string, storage?: Storage) {
    this.storageKey = `env-switcher.package-tree.v${PACKAGE_TREE_CACHE_VERSION}.${encodeURIComponent(cacheKey)}`;
    this.storage = storage ?? this.getBrowserStorage();
  }

  public read(): PackageTreeCacheEntry[] | null {
    const rawCache = this.readRawCache();
    if (!rawCache) {
      return null;
    }

    let parsedCache: unknown;
    try {
      parsedCache = JSON.parse(rawCache);
    } catch (error) {
      console.warn('Unable to parse the cached S3 package tree.', error);
      return null;
    }

    if (!isSerializedPackageTree(parsedCache)
        || Date.now() - parsedCache.fetchedAt > PACKAGE_TREE_CACHE_TTL_MS) {
      return null;
    }

    return parsedCache.entries.map(entry => ({
      branch: this.deserializePackageDetails(entry.branch),
      versions: entry.versions.map(version => this.deserializePackageDetails(version)),
    }));
  }

  public write(entries: readonly PackageTreeCacheEntry[]): void {
    if (!this.storage) {
      return;
    }

    const cache: SerializedPackageTree = {
      version: PACKAGE_TREE_CACHE_VERSION,
      fetchedAt: Date.now(),
      entries: entries.map(entry => ({
        branch: this.serializePackageDetails(entry.branch),
        versions: entry.versions.map(version => this.serializePackageDetails(version)),
      })),
    };

    try {
      this.storage.setItem(this.storageKey, JSON.stringify(cache));
    } catch (error) {
      console.warn('Unable to persist the S3 package tree cache.', error);
    }
  }

  private readRawCache(): string | null {
    if (!this.storage) {
      return null;
    }

    try {
      return this.storage.getItem(this.storageKey);
    } catch (error) {
      console.warn('Unable to read the cached S3 package tree.', error);
      return null;
    }
  }

  private getBrowserStorage(): Storage | null {
    try {
      return window.localStorage;
    } catch (error) {
      console.warn('Unable to access local storage for the S3 package tree cache.', error);
      return null;
    }
  }

  private serializePackageDetails(packageDetails: PackageDetails): SerializedPackageDetails {
    return {
      folder: packageDetails.folder,
      label: packageDetails.label,
      timestamp: packageDetails.timestamp?.getTime(),
      dateLabel: packageDetails.dateLabel,
    };
  }

  private deserializePackageDetails(packageDetails: SerializedPackageDetails): PackageDetails {
    return {
      folder: packageDetails.folder,
      label: packageDetails.label,
      timestamp: packageDetails.timestamp === undefined
        ? undefined
        : new Date(packageDetails.timestamp),
      dateLabel: packageDetails.dateLabel,
    };
  }
}
